export class StateMenuLifecycle {
  public readonly didClose: Promise<void>;

  private closeOperation: Promise<boolean> | null = null;
  private transition: Promise<void> | null = null;
  private resolveTransition: (() => void) | null = null;
  private resolveDidClose: () => void = (): void => undefined;
  private transitionCompletionTask: ReturnType<typeof setTimeout> | null = null;
  private closed: boolean = false;
  private destroyed: boolean = false;

  private readonly handleWillTransition = (event: Event): void => {
    if (event.target === this.element) this.startTransition();
  };

  private readonly handleDidOpen = (event: Event): void => {
    if (event.target === this.element) this.finishTransitionAfterEvent();
  };

  private readonly handleDidClose = (event: Event): void => {
    if (event.target !== this.element) return;

    this.closed = true;
    this.destroy();
    // Gesture completion dispatches this event from an Ionic animation callback.
    setTimeout(this.resolveDidClose, 0);
  };

  public get isClosed(): boolean {
    return this.closed;
  }

  constructor(private readonly element: HTMLIonMenuElement) {
    this.didClose = new Promise<void>((resolve): void => {
      this.resolveDidClose = resolve;
    });
    element.addEventListener('ionWillOpen', this.handleWillTransition);
    element.addEventListener('ionWillClose', this.handleWillTransition);
    element.addEventListener('ionDidOpen', this.handleDidOpen);
    element.addEventListener('ionDidClose', this.handleDidClose);
    // Registration runs synchronously inside the first ionWillOpen event.
    this.startTransition();
  }

  public async close(): Promise<boolean> {
    if (this.closeOperation) return await this.closeOperation;

    const operation: Promise<boolean> = this.closeWhenReady();
    this.closeOperation = operation;
    try {
      return await operation;
    } finally {
      this.closeOperation = null;
    }
  }

  public destroy(): void {
    if (this.destroyed) return;

    this.destroyed = true;
    this.cancelTransitionCompletion();
    this.element.removeEventListener('ionWillOpen', this.handleWillTransition);
    this.element.removeEventListener('ionWillClose', this.handleWillTransition);
    this.element.removeEventListener('ionDidOpen', this.handleDidOpen);
    this.element.removeEventListener('ionDidClose', this.handleDidClose);
    this.finishTransition();
  }

  private async closeWhenReady(): Promise<boolean> {
    while (!this.destroyed) {
      if (this.transition) await this.transition;
      if (this.closed) return true;
      if (this.destroyed) return false;

      if (await this.element.close() || this.closed) return true;
      if (!this.transition) return false;
    }
    return this.closed;
  }

  private startTransition(): void {
    this.cancelTransitionCompletion();
    if (this.transition || this.destroyed) return;

    this.transition = new Promise<void>((resolve): void => {
      this.resolveTransition = resolve;
    });
  }

  private finishTransitionAfterEvent(): void {
    this.cancelTransitionCompletion();
    // Ionic resets its animation callbacks after dispatching ionDidOpen. A new
    // close must start in the next task, not re-enter that unfinished callback.
    this.transitionCompletionTask = setTimeout((): void => {
      this.transitionCompletionTask = null;
      this.finishTransition();
    }, 0);
  }

  private cancelTransitionCompletion(): void {
    if (this.transitionCompletionTask === null) return;

    clearTimeout(this.transitionCompletionTask);
    this.transitionCompletionTask = null;
  }

  private finishTransition(): void {
    const resolve: (() => void) | null = this.resolveTransition;
    this.transition = null;
    this.resolveTransition = null;
    resolve?.();
  }
}
